
/**
 * VisualStudio — per-draft, per-channel AI image studio inside the Planner.
 *
 *   Generate · Upload · From website
 *
 * Sits inside DraftCard (one studio per draft). Lets the editor:
 *   · type a prompt and get 1-4 variants from Gemini (Nano Banana 2) or
 *     OpenAI (GPT Image 1) at the platform-correct aspect ratio,
 *   · click "Auto-prompt" to have Claude write the prompt from the draft body,
 *   · re-prompt an existing variant to edit it,
 *   · upload a photo and (optionally) run an AI enhance pass,
 *   · pick a curated screenshot of hidroverse.co.za as the starting point
 *     and enhance it.
 *
 * Every result is centre-cropped + resized server-side to the exact
 * platform size, then surfaced inline. Hitting "Attach" writes the asset
 * back to the draft so it appears in the main gallery alongside the
 * seeded launch-week assets.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, Upload, Globe, Wand2, RefreshCw, Plus, X, Type } from "lucide-react";
import OverlayEditor from "./OverlayEditor";

const API = process.env.REACT_APP_BACKEND_URL;

const FORMATS = [
  { key: "ig-square",   label: "Instagram Feed",     sub: "1080×1080" },
  { key: "ig-portrait", label: "Instagram Portrait", sub: "1080×1350" },
  { key: "story",       label: "Story / Reel",       sub: "1080×1920" },
  { key: "li-wide",     label: "LinkedIn Feed",      sub: "1200×627" },
  { key: "li-square",   label: "LinkedIn Square",    sub: "1200×1200" },
  { key: "fb-wide",     label: "Facebook",           sub: "1200×627" },
];

// Default format per channel angle.
const DEFAULT_FORMAT = {
  instagram: "ig-portrait",
  "linkedin-text": "li-wide",
  "linkedin-carousel": "li-square",
  "linkedin-video": "li-wide",
  facebook: "fb-wide",
};

const PROVIDERS = [
  { key: "gemini", label: "Gemini Nano Banana 2", sub: "default · supports edit + enhance" },
  { key: "openai", label: "OpenAI GPT Image 1",   sub: "stronger marketing graphics · text-to-image only" },
];

export function VisualStudio({ draft, apiToken, onAttached, hook = "" }) {
  const [tab, setTab] = useState("design");      // "design" | "generate" | "upload" | "website" | "overlay"
  const [format, setFormat] = useState(DEFAULT_FORMAT[draft.angle] || "ig-square");
  const [provider, setProvider] = useState("gemini");
  const [prompt, setPrompt] = useState("");
  const [variants, setVariants] = useState(2);
  const [working, setWorking] = useState("");      // any non-empty = busy
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);      // [{url, provider, prompt, format}]
  const [siteShots, setSiteShots] = useState([]);
  const [attaching, setAttaching] = useState(null);

  // ─── Designer (one-prompt, brand-locked) state ─────────────────────
  // The brief seeds from the DRAFT'S OWN hook + body so the marketer
  // doesn't retype the message they just wrote. Editable from there.
  const _seedBrief = () => {
    const hook = (draft.hook || "").trim();
    const body = (draft.body || "").trim();
    if (!hook && !body) return "";
    const out = [];
    if (hook) out.push(`Tagline: "${hook}"`);
    if (body) out.push(body.slice(0, 600));
    return out.join("\n\n");
  };
  const [designBrief, setDesignBrief] = useState(_seedBrief());
  const _lastSeedRef = React.useRef(draft.id);
  React.useEffect(() => {
    if (_lastSeedRef.current !== draft.id) {
      _lastSeedRef.current = draft.id;
      setDesignBrief(_seedBrief());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id]);
  const [designPhoto, setDesignPhoto] = useState(null);     // File object
  const [designVariants, setDesignVariants] = useState(6);
  const [designAiPhoto, setDesignAiPhoto] = useState(false);  // Model A toggle
  // Track templates we've already shown so "Try another variation"
  // can ask the art director for layouts we haven't seen yet.
  const [shownTemplates, setShownTemplates] = useState([]);

  const designerFileRef = React.useRef(null);

  const _runDesign = async ({ exclude = [] } = {}) => {
    if (!designBrief.trim()) { setError("Type a brief first."); return; }
    setError(""); setWorking("design");
    try {
      const fd = new FormData();
      fd.append("brief", designBrief.trim());
      fd.append("format", format);
      fd.append("variants", String(designVariants));
      fd.append("draft_id", draft.id);
      // Model A — generate brand-aware editorial base photos via Nano
      // Banana when the editor hasn't uploaded a photo. Ignored when a
      // photo IS uploaded (the uploaded photo is sacred).
      fd.append("ai_photo", designAiPhoto && !designPhoto ? "true" : "false");
      if (designPhoto) fd.append("file", designPhoto);
      if (exclude.length) fd.append("exclude_templates", exclude.join(","));
      // 1) Submit the job — returns immediately with a job_id so we never
      //    hold an HTTP request open long enough for Cloudflare's 100 s
      //    proxy timeout to fire HTTP 520 at us.
      const r = await fetch(`${API}/api/content-calendar/visuals/design`, {
        method: "POST", headers: auth, body: fd,
      });
      const submitText = await r.text();
      let submit = {};
      try { submit = JSON.parse(submitText); } catch (_) { submit = { detail: submitText.slice(0, 600) }; }
      if (!r.ok || !submit.job_id) {
        throw new Error(submit.detail || `Designer failed (HTTP ${r.status})`);
      }
      // 2) Poll up to ~5 minutes — Chromium first-install can take 90 s,
      //    Claude vision + 3 renders another 60 s. Comfortably inside.
      const jobId = submit.job_id;
      const deadline = Date.now() + 5 * 60 * 1000;
      let result = null;
      while (Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 2000));
        const pr = await fetch(`${API}/api/content-calendar/visuals/design/${jobId}`,
          { headers: auth });
        const pj = await pr.json();
        if (pj.status === "done") { result = pj; break; }
        if (pj.status === "failed") {
          throw new Error(pj.error || "Designer failed");
        }
      }
      if (!result) throw new Error("Designer timed out (5 minutes). Try fewer variants or retry.");
      const stamped = (result.results || []).map((row) => ({
        id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        url: row.url, provider: "designer",
        prompt: `${row.spec.template} · ${row.spec.headline}`,
        format: row.format, source: "designer", source_url: null,
        spec: row.spec,
      }));
      setResults((prev) => [...stamped, ...prev]);
      const newTpls = stamped.map((s) => s.spec?.template).filter(Boolean);
      setShownTemplates((prev) => Array.from(new Set([...prev, ...newTpls])));
    } catch (e) { setError(e.message); }
    finally { setWorking(""); }
  };

  const runDesign = () => {
    // Fresh design — reset the seen-template list.
    setShownTemplates([]);
    return _runDesign({ exclude: [] });
  };

  const tryAnotherVariation = () => _runDesign({ exclude: shownTemplates });

  const auth = { Authorization: `Bearer ${apiToken}` };
  const json = { ...auth, "Content-Type": "application/json" };

  useEffect(() => {
    fetch(`${API}/api/content-calendar/visuals/site-shots`, { headers: auth })
      .then((r) => r.json())
      .then((d) => setSiteShots(d.shots || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const callGenerate = async (body) => {
    setError("");
    setWorking("generate");
    try {
      const r = await fetch(`${API}/api/content-calendar/visuals/generate`, {
        method: "POST", headers: json, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Generation failed");
      const stamped = (d.urls || []).map((url) => ({
        // Stable per-result id — prevents React key collisions when the
        // backend dedupes to an identical URL across multiple variants.
        id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        url, provider: d.provider, prompt: d.prompt, format: d.format,
        source: body.source_url ? "edit" : "generate",
        // Track the source URL so the UI can show "edited from this tile"
        // — closes the loop on the "wrong image used" confusion.
        source_url: body.source_url || null,
      }));
      setResults((prev) => [...stamped, ...prev]);
    } catch (e) { setError(e.message); }
    finally { setWorking(""); }
  };

  const generate = async () => {
    if (!prompt.trim()) { setError("Add a prompt first."); return; }
    await callGenerate({
      prompt: prompt.trim(),
      provider, format, variants,
      draft_id: draft.id,
    });
  };

  const reprompt = async (item, instruction) => {
    if (!instruction.trim()) return;
    // Edit only supported on Gemini.
    await callGenerate({
      prompt: instruction.trim(),
      provider: "gemini", format, variants: 1,
      draft_id: draft.id,
      source_url: item.url,
    });
  };

  const composeWithHook = async (item, customHeadline) => {
    const headline = (customHeadline || hook || "").trim();
    if (!headline) {
      setError("Add a hook first (top of draft) — the composer needs a headline.");
      return;
    }
    setError("");
    setWorking("compose");
    try {
      const r = await fetch(`${API}/api/content-calendar/visuals/compose`, {
        method: "POST", headers: json,
        body: JSON.stringify({
          source_url: item.url,
          format: item.format || format,
          headline,
          draft_id: draft.id,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Compose failed");
      setResults((prev) => [{
        id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        url: d.url, provider: "compose", prompt: `Overlay: ${headline}`,
        format: d.format, source: "compose",
        source_url: item.url,
      }, ...prev]);
    } catch (e) { setError(e.message); }
    finally { setWorking(""); }
  };

  const autoPrompt = async () => {
    setError("");
    setWorking("auto");
    try {
      const r = await fetch(`${API}/api/content-calendar/visuals/auto-prompt`, {
        method: "POST", headers: json,
        body: JSON.stringify({ draft_id: draft.id, format }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Auto-prompt failed");
      setPrompt(d.prompt || "");
    } catch (e) { setError(e.message); }
    finally { setWorking(""); }
  };

  const handleUpload = async (file, enhance) => {
    if (!file) return;
    setError("");
    setWorking("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("format", format);
      const r = await fetch(`${API}/api/content-calendar/visuals/upload`, {
        method: "POST", headers: auth, body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Upload failed");
      const item = {
        id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        url: d.url, provider: "upload", prompt: "uploaded",
        format: d.format, source: "upload", source_url: null,
      };
      setResults((prev) => [item, ...prev]);
      if (enhance) {
        await callGenerate({
          prompt: prompt.trim() || "Enhance this image. Improve lighting, sharpness and colour balance while preserving subject and composition. Match the HidroVerse charcoal/cyan brand aesthetic.",
          provider: "gemini", format, variants: 1,
          draft_id: draft.id, source_url: d.url,
        });
      }
    } catch (e) { setError(e.message); }
    finally { setWorking(""); }
  };

  const enhanceSiteShot = async (shot) => {
    const instr = prompt.trim() ||
      "Re-render this website screenshot as an editorial social-card composition. Keep the layout recognisable but enhance lighting and colour to match the HidroVerse charcoal/cyan brand palette. Photo-realistic.";
    await callGenerate({
      prompt: instr,
      provider: "gemini", format, variants: 1,
      draft_id: draft.id, source_url: shot.url,
    });
  };

  const pickAsSource = (url) => {
    // Attach directly to the draft's gallery.
    attach({ url, label: "Studio asset", format, source: "studio",
             provider: "studio", prompt });
  };

  const attach = useCallback(async (item) => {
    setAttaching(item.url);
    try {
      const r = await fetch(`${API}/api/content-calendar/visuals/attach`, {
        method: "POST", headers: json, body: JSON.stringify({
          draft_id: draft.id, url: item.url, label: item.label || "",
          format: item.format || format,
          source: item.source || "studio",
          provider: item.provider || "",
          prompt: item.prompt || "",
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not attach");
      if (onAttached) await onAttached();
    } catch (e) { setError(e.message); }
    finally { setAttaching(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.id, format]);

  return (
    <div className="hv-card-soft p-4 mt-4" data-testid={`cc-visual-studio-${draft.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wand2 size={14} style={{ color: "#0E7A82" }} />
          <span className="font-mono uppercase"
                style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                         color: "#0E7A82", fontWeight: 700 }}>
            Visual Studio — generate, edit, upload
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 flex-wrap">
        <TabBtn active={tab === "design"}   onClick={() => setTab("design")}   testid="vs-tab-design"><Wand2     size={11} /> Design (1-prompt)</TabBtn>
        <TabBtn active={tab === "generate"} onClick={() => setTab("generate")} testid="vs-tab-generate"><Sparkles size={11} /> Generate</TabBtn>
        <TabBtn active={tab === "upload"}   onClick={() => setTab("upload")}   testid="vs-tab-upload"><Upload    size={11} /> Upload + enhance</TabBtn>
        <TabBtn active={tab === "website"}  onClick={() => setTab("website")}  testid="vs-tab-website"><Globe     size={11} /> From website</TabBtn>
        <TabBtn active={tab === "overlay"}  onClick={() => setTab("overlay")}  testid="vs-tab-overlay"><Type      size={11} /> Edit overlays</TabBtn>
      </div>

      {/* Per-platform aspect-ratio quick row — applies to ALL tabs */}
      <div className="flex items-center gap-2 mb-3 flex-wrap"
           data-testid={`vs-format-row-${draft.id}`}>
        <span className="font-mono uppercase"
              style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                       color: "rgba(12,42,64,0.55)", fontWeight: 700,
                       marginRight: 4 }}>
          Format
        </span>
        {FORMATS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFormat(f.key)}
            data-testid={`vs-format-btn-${f.key}-${draft.id}`}
            className="font-mono uppercase"
            style={{
              padding: "6px 12px",
              fontSize: "0.62rem",
              letterSpacing: "0.14em",
              fontWeight: 700,
              border: format === f.key
                ? "1px solid #1FB6C1"
                : "1px solid rgba(12,42,64,0.18)",
              background: format === f.key ? "#1FB6C1" : "#FFFFFF",
              color: format === f.key ? "#0C2A40" : "rgba(12,42,64,0.78)",
              borderRadius: 999,
              cursor: "pointer",
              lineHeight: 1.1,
            }}
            title={`${f.label} · ${f.sub}`}
          >
            {f.label.replace("Instagram", "IG")
                    .replace("LinkedIn",  "LI")
                    .replace("Facebook",  "FB")
                    .replace(" Portrait", " Port")
                    .replace(" Square",   " Sq")
                    .replace(" Wide",     " Wide")
                    .replace(" Feed",     " Feed")
                    .replace("Story / Reel", "Story")}
          </button>
        ))}
      </div>

      {/* Provider picker — only visible on Generate tab */}
      {tab === "generate" && (
        <div className="mb-3" style={{ maxWidth: 340 }}>
          <Select label="Model" value={provider} onChange={setProvider} testid={`vs-provider-${draft.id}`}>
            {PROVIDERS.map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </Select>
        </div>
      )}

      {/* Tab: Design (default, one-prompt brand-locked, Holo-style) */}
      {tab === "design" && (
        <div>
          <div className="font-mono uppercase mb-1"
               style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                        color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
            Brief
          </div>
          <textarea
            value={designBrief}
            onChange={(e) => setDesignBrief(e.target.value)}
            rows={3}
            placeholder='Describe the post and the message in plain English. Example: "Social post about HidroVerse taking water from abstraction to distribution. Use the borehole team photo. Tagline: From abstraction to distribution."'
            data-testid={`vs-design-brief-${draft.id}`}
            className="hv-input w-full"
            style={{ resize: "vertical" }}
          />

          {/* ─── Two-model picker (Holo-style) ─────────────────────── */}
          {/* MODE A: editor uploaded a photo → we wrap brand chrome around
                     it untouched. MODE B: no photo → optionally call Nano
                     Banana to generate brand-aware base photos first. */}
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* MODE A — Upload my photo */}
            <button type="button"
              onClick={() => designerFileRef.current?.click()}
              data-testid={`vs-design-mode-upload-${draft.id}`}
              className="text-left p-3"
              style={{
                background: designPhoto ? "rgba(31,182,193,0.08)" : "#FAFAF7",
                border: `2px solid ${designPhoto ? "rgba(31,182,193,0.55)" : "rgba(12,42,64,0.14)"}`,
                borderRadius: 10, cursor: "pointer",
              }}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-mono uppercase"
                      style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                               color: designPhoto ? "#0E7A82" : "rgba(12,42,64,0.55)",
                               fontWeight: 700 }}>
                  {designPhoto ? "✓ Using your photo" : "Use my photo"}
                </span>
                <Upload size={12} style={{ color: designPhoto ? "#0E7A82" : "rgba(12,42,64,0.5)" }} />
              </div>
              <div style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.78)", lineHeight: 1.45 }}>
                {designPhoto ? (
                  <>
                    <strong>{designPhoto.name}</strong> · {(designPhoto.size / 1024).toFixed(0)} KB<br />
                    Photo pixels untouched — light enhance only, brand chrome around it.
                  </>
                ) : (
                  <>Upload a real site photo (borehole, plant room, RO bench).<br />
                  We just add the logo, headline and CTA — no AI re-imagining.</>
                )}
              </div>
              <input
                ref={designerFileRef}
                type="file"
                accept="image/*"
                onChange={(e) => { setDesignPhoto(e.target.files?.[0] || null);
                                   if (e.target.files?.[0]) setDesignAiPhoto(false); }}
                data-testid={`vs-design-file-${draft.id}`}
                style={{ display: "none" }}
              />
              {designPhoto && (
                <button type="button"
                        onClick={(e) => { e.stopPropagation();
                          setDesignPhoto(null);
                          if (designerFileRef.current) designerFileRef.current.value = ""; }}
                        data-testid={`vs-design-clear-photo-${draft.id}`}
                        className="hv-pill mt-2"
                        style={{ fontSize: "0.68rem" }}>
                  <X size={10} /> Clear photo
                </button>
              )}
            </button>

            {/* MODE B — AI-generate photo */}
            <button type="button"
              onClick={() => {
                if (designPhoto) return;     // disabled when a real photo is in
                setDesignAiPhoto((v) => !v);
              }}
              disabled={!!designPhoto}
              data-testid={`vs-design-mode-aiphoto-${draft.id}`}
              className="text-left p-3"
              style={{
                background: designAiPhoto && !designPhoto ? "rgba(31,182,193,0.08)" : "#FAFAF7",
                border: `2px solid ${designAiPhoto && !designPhoto ? "rgba(31,182,193,0.55)" : "rgba(12,42,64,0.14)"}`,
                borderRadius: 10, cursor: designPhoto ? "not-allowed" : "pointer",
                opacity: designPhoto ? 0.55 : 1,
              }}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-mono uppercase"
                      style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                               color: designAiPhoto && !designPhoto ? "#0E7A82" : "rgba(12,42,64,0.55)",
                               fontWeight: 700 }}>
                  {designAiPhoto && !designPhoto ? "✓ AI photo on" : "Generate AI photo"}
                </span>
                <Sparkles size={12} style={{ color: designAiPhoto && !designPhoto ? "#0E7A82" : "rgba(12,42,64,0.5)" }} />
              </div>
              <div style={{ fontSize: "0.78rem", color: "rgba(12,42,64,0.78)", lineHeight: 1.45 }}>
                {designPhoto
                  ? "Disabled — you've uploaded a real photo above."
                  : (designAiPhoto
                      ? <>Nano Banana 2 generates editorial water-infra photos.<br />Brand chrome added on top by HTML/CSS — no fake logos, ever.</>
                      : <>No photo? Toggle this on and we&apos;ll generate brand-aware<br />base photos via Nano Banana before adding the design.</>)}
              </div>
            </button>
          </div>

          <div className="mt-3 p-2"
               style={{ background: "rgba(31,182,193,0.05)",
                        border: "1px solid rgba(31,182,193,0.20)",
                        borderRadius: 8, fontSize: "0.72rem",
                        color: "rgba(12,42,64,0.72)", lineHeight: 1.45 }}>
              <strong>Two models, never hallucinate:</strong> Model A (Nano Banana 2) makes the photo only.
              Model B (HTML/CSS) wraps the design — real Montserrat, real wordmark PNG, brand-palette colours. The logo is never invented.
          </div>

          <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <Select label="Variations" value={String(designVariants)}
                      onChange={(v) => setDesignVariants(parseInt(v, 10))}
                      testid={`vs-design-variants-${draft.id}`} inline>
                {[3, 6, 9, 12, 15].map((n) => <option key={n} value={n}>{n}</option>)}
              </Select>
              {shownTemplates.length > 0 && (
                <span className="font-mono uppercase"
                      data-testid={`vs-design-shown-count-${draft.id}`}
                      style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                               color: "rgba(12,42,64,0.55)", fontWeight: 600 }}>
                  {shownTemplates.length} layouts shown
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {shownTemplates.length > 0 && (
                <button type="button"
                        onClick={tryAnotherVariation}
                        disabled={!!working}
                        data-testid={`vs-design-try-another-${draft.id}`}
                        className="hv-pill">
                  <RefreshCw size={11} />
                  {working === "design" ? "Designing…" : `+${designVariants} more`}
                </button>
              )}
              <button type="button"
                      onClick={runDesign}
                      disabled={!!working || !designBrief.trim()}
                      data-testid={`vs-design-run-${draft.id}`}
                      className="hv-btn">
                <Wand2 size={12} />
                {working === "design" && shownTemplates.length === 0
                  ? (designAiPhoto && !designPhoto
                      ? `Designing ${designVariants} (AI photo + design)…`
                      : `Designing ${designVariants}…`)
                  : `Generate ${designVariants} variations`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Generate */}
      {tab === "generate" && (
        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="font-mono uppercase"
                 style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                          color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
              Prompt
            </div>
            <button type="button"
                    onClick={autoPrompt}
                    disabled={!!working}
                    data-testid={`vs-auto-prompt-${draft.id}`}
                    className="hv-pill">
              <Sparkles size={10} /> Auto-prompt from draft
            </button>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            placeholder="Describe what you want to see — e.g. 'A submersible borehole pump being craned out at golden hour, Mpumalanga bushveld in the background, photo-realistic, editorial engineering aesthetic.'"
            data-testid={`vs-prompt-${draft.id}`}
            className="hv-input w-full"
            style={{ resize: "vertical" }}
          />
          <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
            <Select label="Variants" value={String(variants)} onChange={(v) => setVariants(parseInt(v, 10))} testid={`vs-variants-${draft.id}`} inline>
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
            </Select>
            <button type="button"
                    onClick={generate}
                    disabled={!!working || !prompt.trim()}
                    data-testid={`vs-generate-${draft.id}`}
                    className="hv-btn">
              <Sparkles size={12} />
              {working === "generate" ? "Generating…" : `Generate ${variants}`}
            </button>
          </div>
        </div>
      )}

      {/* Tab: Upload */}
      {tab === "upload" && (
        <div className="hv-card-soft p-4 flex flex-col items-center text-center gap-3"
             style={{ borderStyle: "dashed", borderColor: "rgba(12,42,64,0.20)" }}>
          <Upload size={20} style={{ color: "#0E7A82" }} />
          <div style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.75)" }}>
            Drop a JPEG/PNG (max 20 MB) or click to pick.
            <br />
            Tick &ldquo;Enhance with AI&rdquo; to send it through Gemini Nano Banana 2 for a brand-aligned pass.
          </div>
          <input type="file" accept="image/*"
                 onChange={(e) => handleUpload(e.target.files?.[0], false)}
                 data-testid={`vs-upload-file-${draft.id}`} />
          <button type="button"
                  onClick={() => {
                    const i = document.querySelector(`[data-testid="vs-upload-enhance-input-${draft.id}"]`);
                    i && i.click();
                  }}
                  disabled={!!working}
                  data-testid={`vs-upload-enhance-${draft.id}`}
                  className="hv-pill">
            <Sparkles size={11} /> Upload + AI enhance
          </button>
          <input
            type="file" accept="image/*" style={{ display: "none" }}
            data-testid={`vs-upload-enhance-input-${draft.id}`}
            onChange={(e) => handleUpload(e.target.files?.[0], true)}
          />
        </div>
      )}

      {/* Tab: From website */}
      {tab === "website" && (
        <div>
          <p className="font-light mb-2"
             style={{ fontSize: "0.82rem", color: "rgba(12,42,64,0.70)",
                      lineHeight: 1.5 }}>
            Pick a captured page from hidroverse.co.za as a starting point, then
            (optionally) write a prompt to enhance / re-style it.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {siteShots.map((s) => (
              <div key={s.slug} className="hv-card-soft p-2 is-hoverable"
                   data-testid={`vs-shot-${s.slug}-${draft.id}`}>
                <img src={s.url} alt={s.label} loading="lazy"
                     style={{ width: "100%", height: 80, objectFit: "cover",
                              borderRadius: 6 }} />
                <div className="mt-1 flex items-center justify-between gap-1">
                  <span className="font-mono uppercase"
                        style={{ fontSize: "0.55rem", letterSpacing: "0.16em",
                                 color: "rgba(12,42,64,0.65)" }}>
                    {s.label}
                  </span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => pickAsSource(s.url)}
                            disabled={!!working}
                            data-testid={`vs-shot-use-${s.slug}-${draft.id}`}
                            className="hv-pill">
                      Use
                    </button>
                    <button type="button" onClick={() => enhanceSiteShot(s)}
                            disabled={!!working}
                            data-testid={`vs-shot-enhance-${s.slug}-${draft.id}`}
                            className="hv-pill is-active">
                      <Sparkles size={10} /> Enhance
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Edit overlays — the deterministic compositor */}
      {tab === "overlay" && (
        <OverlayEditor
          sourceUrl={results[0]?.url || null}
          format={format}
          draftId={draft.id}
          apiToken={apiToken}
          initialTitle={hook || ""}
          onRendered={(url) => {
            setResults((prev) => [{
              id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              url, provider: "compose-overlays",
              prompt: "Overlay compositor",
              format,
              source: "compose-overlays",
            }, ...prev]);
          }}
        />
      )}

      {error && (
        <div className="mt-3 px-3 py-2"
             style={{ background: "rgba(184,92,56,0.10)",
                      border: "1px solid rgba(184,92,56,0.35)",
                      color: "#B85C38", fontSize: "0.82rem",
                      borderRadius: 10 }}
             data-testid={`vs-error-${draft.id}`}>
          {error}
        </div>
      )}

      {/* Results gallery — Holo-style grid for 15+ variations */}
      {results.length > 0 && (
        <div className="mt-4">
          <div className="font-mono uppercase mb-2"
               style={{ fontSize: "0.58rem", letterSpacing: "0.22em",
                        color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
            Studio output · {results.length}
          </div>
          <div className="grid gap-3"
               data-testid={`vs-results-grid-${draft.id}`}
               style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
            {results.map((r) => (
              <ResultTile
                key={r.id || r.url}
                item={r}
                draftId={draft.id}
                onAttach={() => attach({ ...r, label: "Studio asset" })}
                onReprompt={(instr) => reprompt(r, instr)}
                onCompose={(headline) => composeWithHook(r, headline)}
                onDelete={() => setResults((prev) => prev.filter((x) => (x.id || x.url) !== (r.id || r.url)))}
                hook={hook}
                attaching={attaching === r.url}
                busy={!!working}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── small subcomponents ─────────────────────────────────────────── */

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
            className={`hv-pill ${active ? "is-active" : ""}`}>
      {children}
    </button>
  );
}

function Select({ label, value, onChange, children, testid, inline }) {
  return (
    <div className={inline ? "inline-flex items-center gap-2" : ""}>
      {label && (
        <div className="font-mono uppercase mb-1"
             style={{ fontSize: "0.55rem", letterSpacing: "0.20em",
                      color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
          {label}
        </div>
      )}
      <select value={value} onChange={(e) => onChange(e.target.value)}
              data-testid={testid}
              className="hv-input"
              style={{ paddingTop: 6, paddingBottom: 6, cursor: "pointer" }}>
        {children}
      </select>
    </div>
  );
}

function ResultTile({ item, draftId, onAttach, onReprompt, onCompose, onDelete, hook, attaching, busy }) {
  const [showRefine, setShowRefine] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [instr, setInstr] = useState("");
  const [headline, setHeadline] = useState(hook || "");
  return (
    <div className="hv-card-soft is-hoverable" style={{ padding: 8, position: "relative" }}>
      {/* Per-tile delete — clears from local results so bad renders
          don't clutter the grid.  We don't purge GridFS here because
          the asset might be referenced elsewhere; this just removes
          the row from the studio output. */}
      <button type="button"
              onClick={onDelete}
              data-testid={`vs-result-delete-${item.id || item.url}`}
              disabled={busy}
              title="Remove this render from the grid"
              className="hv-pill"
              style={{
                position: "absolute", top: 6, right: 6, zIndex: 2,
                padding: "4px 6px", background: "rgba(255,255,255,0.92)",
                border: "1px solid rgba(184,92,56,0.40)", color: "#B85C38",
                borderRadius: 999,
              }}>
        <X size={11} />
      </button>
      {/* "Edited from" mini-thumb — surfaces which tile was used as the
          source when this image was produced via re-prompt or compose.
          Closes the loop on "which image did Gemini start from?". */}
      {item.source_url && (
        <div
          data-testid={`vs-result-source-thumb-${item.id || item.url}`}
          title="Edited from this source"
          style={{
            position: "absolute", top: 6, left: 6, zIndex: 2,
            width: 36, height: 36, borderRadius: 6, overflow: "hidden",
            border: "1.5px solid #1FB6C1",
            boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
          }}>
          <img src={item.source_url} alt=""
               style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}
      <a href={item.url} target="_blank" rel="noreferrer"
         style={{ display: "block", lineHeight: 0 }}>
        <img src={item.url} alt="" loading="lazy"
             style={{ width: "100%", height: 200, objectFit: "contain",
                      background: "#16181B", borderRadius: 8 }} />
      </a>
      <div className="mt-2 flex items-center justify-between gap-1 flex-wrap">
        <span className="font-mono"
              style={{ fontSize: "0.6rem", color: "rgba(12,42,64,0.55)" }}>
          {item.provider} · {item.format}
        </span>
        <div className="flex gap-1 flex-wrap">
          <button type="button"
                  onClick={() => { setShowRefine((s) => !s); setShowCompose(false); }}
                  data-testid={`vs-result-reprompt-${draftId}`}
                  disabled={busy}
                  className="hv-pill">
            <RefreshCw size={10} /> Re-prompt
          </button>
          <button type="button"
                  onClick={() => {
                    setShowCompose((s) => !s);
                    setShowRefine(false);
                    if (!headline) setHeadline(hook || "");
                  }}
                  data-testid={`vs-result-compose-${draftId}`}
                  disabled={busy || item.source === "compose"}
                  className="hv-pill"
                  title={item.source === "compose"
                    ? "Already composed — re-prompt or attach"
                    : "Drop the hook on this image as a finished social poster"}>
            <Type size={10} /> Compose
          </button>
          <button type="button"
                  onClick={onAttach}
                  disabled={busy || attaching}
                  data-testid={`vs-result-attach-${draftId}`}
                  className="hv-pill is-active">
            <Plus size={10} /> {attaching ? "…" : "Attach"}
          </button>
        </div>
      </div>
      {showRefine && (
        <div className="mt-2">
          <textarea
            value={instr}
            onChange={(e) => setInstr(e.target.value)}
            rows={2}
            placeholder="What should change? e.g. 'cooler lighting, remove the figure on the left, sharpen the pump body'"
            className="hv-input w-full"
            style={{ resize: "vertical", fontSize: "0.78rem" }}
          />
          <div className="mt-1 flex justify-end gap-1">
            <button type="button" onClick={() => setShowRefine(false)}
                    className="hv-pill" disabled={busy}>
              <X size={10} /> Cancel
            </button>
            <button type="button"
                    onClick={() => { onReprompt(instr); setInstr(""); setShowRefine(false); }}
                    disabled={busy || !instr.trim()}
                    className="hv-pill is-active">
              <Sparkles size={10} /> Apply
            </button>
          </div>
        </div>
      )}
      {showCompose && (
        <div className="mt-2"
             data-testid={`vs-compose-panel-${draftId}`}>
          <div className="font-mono uppercase mb-1"
               style={{ fontSize: "0.55rem", letterSpacing: "0.20em",
                        color: "rgba(12,42,64,0.55)", fontWeight: 700 }}>
            Headline (overlay text — pre-filled from draft hook)
          </div>
          <textarea
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            rows={2}
            placeholder="e.g. 'Borehole pull at Thornybush — same-day diagnostic.'"
            className="hv-input w-full"
            data-testid={`vs-compose-headline-${draftId}`}
            style={{ resize: "vertical", fontSize: "0.78rem" }}
          />
          <div className="mt-1 flex justify-end gap-1">
            <button type="button" onClick={() => setShowCompose(false)}
                    className="hv-pill" disabled={busy}>
              <X size={10} /> Cancel
            </button>
            <button type="button"
                    onClick={() => { onCompose(headline); setShowCompose(false); }}
                    disabled={busy || !headline.trim()}
                    data-testid={`vs-compose-apply-${draftId}`}
                    className="hv-pill is-active">
              <Type size={10} /> Compose poster
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
